import { useState } from "react";
import {
  Container,
  Title,
  Text,
  Button,
  Group,
  Stack,
  Alert,
  Modal,
  Textarea,
  Badge,
  Card,
  Grid,
  ActionIcon,
  Tooltip,
} from "@mantine/core";
import {
  IconCheck,
  IconX,
  IconDownload,
  IconFileText,
  IconUser,
  IconCalendar,
} from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import {
  usePendingApprovals,
  useApproveTender,
  useRejectTender,
} from "../hooks/useApproval";
import { useDownloadTorDocument } from "../hooks/useTorDocuments";
import { useAuth } from "../../../auth/AuthContext";
import {
  usePendingReportApprovals,
  useCheckReport,
  useRejectCheck,
  useApproveReport,
  useRejectApproval,
  useApproveByBidCommittee,
  useRejectByBidCommittee,
} from "../hooks/useEvaluationReports";
import {
  usePendingSingleSourceApprovals,
  useApproveSingleSourceForm,
  useRejectSingleSourceForm,
} from "../../singleSource/hooks/useSingleSource";
import {
  usePendingAwardSheetApprovals,
  useApproveAwardSheetApproval,
  useRejectAwardSheetApproval,
  usePendingAgendaReturnFormApprovals,
  useApproveAgendaReturnFormApproval,
  useRejectAgendaReturnFormApproval,
  usePendingCEOEndorsementApprovals,
  useApproveCEOEndorsement,
  useRejectCEOEndorsement,
} from "../../bidCommittee/hooks";
import {
  usePendingProjectSummarySheetApprovals,
  useApprovePeerProjectSummarySheet,
  useApproveHeadProjectSummarySheet,
  useRejectProjectSummarySheet,
} from "../../projectSummarySheet/hooks/useProjectSummarySheet";
import type { ProjectSummarySheetWithPending } from "../../../api/projectSummarySheet";
import type { SingleSourceFormWithPending } from "../../../api/singleSource";
import { approvalDisplayName, dhivehiDisplayStyle } from "../../../utils/dhivehiText";
import dayjs from "dayjs";

/** Badge for an award sheet / return form approval: the chair's and secretary's are their sign-off. */
function signingBadgeLabel(signingAs: string | undefined, memberLabel: string): string {
  if (signingAs === "CHAIR") return "Chairperson approval";
  if (signingAs === "SECRETARY") return "Secretary signature";
  return memberLabel;
}

export function PendingApprovals() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: pendingApprovalsData, isLoading, error } = usePendingApprovals();
  const { data: pendingSingleSourceForms, isLoading: isLoadingSingleSource } = usePendingSingleSourceApprovals(user?.role !== "CEO");
  const { data: pendingAwardSheetApprovals, isLoading: isLoadingAwardSheets } = usePendingAwardSheetApprovals();
  const { data: pendingAgendaReturnFormApprovals, isLoading: isLoadingAgendaReturn } =
    usePendingAgendaReturnFormApprovals();
  const { data: pendingCEOEndorsementApprovals, isLoading: isLoadingCEOEndorsements } = usePendingCEOEndorsementApprovals();
  const { data: pendingProjectSummarySheets, isLoading: isLoadingPss } = usePendingProjectSummarySheetApprovals();
  const [selectedTender, setSelectedTender] = useState<any>(null);
  const [selectedReport, setSelectedReport] = useState<any>(null);
  const [selectedSingleSourceForm, setSelectedSingleSourceForm] = useState<SingleSourceFormWithPending | null>(null);
  const [selectedAwardSheetApproval, setSelectedAwardSheetApproval] = useState<any>(null);
  const [selectedCEOEndorsementApproval, setSelectedCEOEndorsementApproval] = useState<any>(null);
  const [approvalModal, setApprovalModal] = useState(false);
  const [rejectionModal, setRejectionModal] = useState(false);
  const [singleSourceApprovalModal, setSingleSourceApprovalModal] = useState(false);
  const [singleSourceRejectionModal, setSingleSourceRejectionModal] = useState(false);
  const [awardSheetApprovalModal, setAwardSheetApprovalModal] = useState(false);
  const [awardSheetRejectionModal, setAwardSheetRejectionModal] = useState(false);
  const [selectedAgendaReturnApproval, setSelectedAgendaReturnApproval] = useState<any>(null);
  const [agendaReturnApprovalModal, setAgendaReturnApprovalModal] = useState(false);
  const [agendaReturnRejectionModal, setAgendaReturnRejectionModal] = useState(false);
  const [ceoEndorsementApprovalModal, setCeoEndorsementApprovalModal] = useState(false);
  const [ceoEndorsementRejectionModal, setCeoEndorsementRejectionModal] = useState(false);
  const [pssApprovalModal, setPssApprovalModal] = useState(false);
  const [pssRejectionModal, setPssRejectionModal] = useState(false);
  const [selectedPss, setSelectedPss] = useState<ProjectSummarySheetWithPending | null>(null);
  const [pssApproveComments, setPssApproveComments] = useState("");
  const [pssRejectComments, setPssRejectComments] = useState("");
  const [comments, setComments] = useState("");
  const [actionType, setActionType] = useState<'tender' | 'check' | 'approve' | 'bid-committee'>('tender');

  const approveTender = useApproveTender(selectedTender?.id || "");
  const rejectTender = useRejectTender(selectedTender?.id || "");
  const downloadTorDocument = useDownloadTorDocument(selectedTender?.id || "");
  
  const checkReport = useCheckReport();
  const rejectCheck = useRejectCheck();
  const approveReport = useApproveReport();
  const rejectApproval = useRejectApproval();
  const approveByBidCommittee = useApproveByBidCommittee();
  const rejectByBidCommittee = useRejectByBidCommittee();
  const approveSingleSourceForm = useApproveSingleSourceForm();
  const rejectSingleSourceForm = useRejectSingleSourceForm();
  const approveAwardSheetApproval = useApproveAwardSheetApproval();
  const rejectAwardSheetApproval = useRejectAwardSheetApproval();
  const approveAgendaReturnApproval = useApproveAgendaReturnFormApproval();
  const rejectAgendaReturnApproval = useRejectAgendaReturnFormApproval();
  const approveCEOEndorsement = useApproveCEOEndorsement();
  const rejectCEOEndorsement = useRejectCEOEndorsement();
  const approvePeerPss = useApprovePeerProjectSummarySheet();
  const approveHeadPss = useApproveHeadProjectSummarySheet();
  const rejectPss = useRejectProjectSummarySheet();

  const pendingTenders = pendingApprovalsData?.tenders || [];
  const pendingReports = pendingApprovalsData?.evaluationReports || [];
  const pendingSingleSource = pendingSingleSourceForms || [];
  const pendingAwardSheets = pendingAwardSheetApprovals || [];
  const pendingAgendaReturns = pendingAgendaReturnFormApprovals || [];
  const pendingCEOEndorsements = pendingCEOEndorsementApprovals || [];
  const pendingPss = pendingProjectSummarySheets || [];
  const totalPending =
    pendingTenders.length +
    pendingReports.length +
    pendingSingleSource.length +
    pendingAwardSheets.length +
    pendingAgendaReturns.length +
    pendingCEOEndorsements.length +
    pendingPss.length;

  const handleApprove = () => {
    if (selectedTender) {
      approveTender.mutate(
        { comments: comments.trim() || undefined },
        {
          onSuccess: () => {
            setApprovalModal(false);
            setSelectedTender(null);
            setComments("");
          },
        }
      );
    }
  };

  const handleReject = () => {
    if (selectedTender && comments.trim()) {
      rejectTender.mutate(
        { comments: comments.trim() },
        {
          onSuccess: () => {
            setRejectionModal(false);
            setSelectedTender(null);
            setComments("");
          },
        }
      );
    }
  };

  const handleDownload = () => {
    const torDocument = selectedTender?.documents?.find(
      (doc: any) => doc.type === "TOR"
    );
    if (torDocument) {
      downloadTorDocument.mutate();
    }
  };

  const handleSingleSourceApprove = () => {
    if (selectedSingleSourceForm) {
      approveSingleSourceForm.mutate(
        { id: selectedSingleSourceForm.id, comments: comments.trim() || undefined },
        {
          onSuccess: () => {
            setSingleSourceApprovalModal(false);
            setSelectedSingleSourceForm(null);
            setComments("");
          },
        }
      );
    }
  };

  const handleSingleSourceReject = () => {
    if (selectedSingleSourceForm && comments.trim()) {
      rejectSingleSourceForm.mutate(
        { id: selectedSingleSourceForm.id, comments: comments.trim() },
        {
          onSuccess: () => {
            setSingleSourceRejectionModal(false);
            setSelectedSingleSourceForm(null);
            setComments("");
          },
        }
      );
    }
  };

  const handleAwardSheetApprove = () => {
    if (selectedAwardSheetApproval) {
      approveAwardSheetApproval.mutate(
        { id: selectedAwardSheetApproval.id, data: { comments: comments.trim() || undefined } },
        {
          onSuccess: () => {
            setAwardSheetApprovalModal(false);
            setSelectedAwardSheetApproval(null);
            setComments("");
          },
        }
      );
    }
  };

  const handleAwardSheetReject = () => {
    if (selectedAwardSheetApproval && comments.trim()) {
      rejectAwardSheetApproval.mutate(
        { id: selectedAwardSheetApproval.id, data: { comments: comments.trim() } },
        {
          onSuccess: () => {
            setAwardSheetRejectionModal(false);
            setSelectedAwardSheetApproval(null);
            setComments("");
          },
        }
      );
    }
  };

  const handleAgendaReturnApprove = () => {
    if (selectedAgendaReturnApproval) {
      approveAgendaReturnApproval.mutate(
        { id: selectedAgendaReturnApproval.id, data: { comments: comments.trim() || undefined } },
        {
          onSuccess: () => {
            setAgendaReturnApprovalModal(false);
            setSelectedAgendaReturnApproval(null);
            setComments("");
          },
        }
      );
    }
  };

  const handleAgendaReturnReject = () => {
    if (selectedAgendaReturnApproval && comments.trim()) {
      rejectAgendaReturnApproval.mutate(
        { id: selectedAgendaReturnApproval.id, data: { comments: comments.trim() } },
        {
          onSuccess: () => {
            setAgendaReturnRejectionModal(false);
            setSelectedAgendaReturnApproval(null);
            setComments("");
          },
        }
      );
    }
  };

  const handleCEOEndorsementApprove = () => {
    if (selectedCEOEndorsementApproval) {
      approveCEOEndorsement.mutate(
        { id: selectedCEOEndorsementApproval.endorsement.id, data: { comments: comments.trim() || undefined } },
        {
          onSuccess: () => {
            setCeoEndorsementApprovalModal(false);
            setSelectedCEOEndorsementApproval(null);
            setComments("");
          },
        }
      );
    }
  };

  const handlePssApprove = () => {
    if (!selectedPss) return;
    const isPeer = selectedPss.approval?.stage === "PEER_CHECK";
    if (isPeer) {
      approvePeerPss.mutate(
        { id: selectedPss.id, comments: pssApproveComments.trim() || undefined },
        {
          onSuccess: () => {
            setPssApprovalModal(false);
            setSelectedPss(null);
            setPssApproveComments("");
          },
        }
      );
    } else {
      approveHeadPss.mutate(
        { id: selectedPss.id, comments: pssApproveComments.trim() || undefined },
        {
          onSuccess: () => {
            setPssApprovalModal(false);
            setSelectedPss(null);
            setPssApproveComments("");
          },
        }
      );
    }
  };

  const handlePssReject = () => {
    if (!selectedPss || !pssRejectComments.trim()) return;
    rejectPss.mutate(
      { id: selectedPss.id, comments: pssRejectComments.trim() },
      {
        onSuccess: () => {
          setPssRejectionModal(false);
          setSelectedPss(null);
          setPssRejectComments("");
        },
      }
    );
  };

  const handleCEOEndorsementReject = () => {
    if (selectedCEOEndorsementApproval && comments.trim()) {
      rejectCEOEndorsement.mutate(
        { id: selectedCEOEndorsementApproval.endorsement.id, data: { comments: comments.trim() } },
        {
          onSuccess: () => {
            setCeoEndorsementRejectionModal(false);
            setSelectedCEOEndorsementApproval(null);
            setComments("");
          },
        }
      );
    }
  };

  const handleReportApprove = () => {
    if (!selectedReport) return;
    
    const reportId = selectedReport.id;
    const data = { comments: comments.trim() || undefined };
    
    if (actionType === 'check') {
      checkReport.mutate({ reportId, data }, {
        onSuccess: () => {
          setApprovalModal(false);
          setSelectedReport(null);
          setComments("");
        },
      });
    } else if (actionType === 'approve') {
      approveReport.mutate({ reportId, data }, {
        onSuccess: () => {
          setApprovalModal(false);
          setSelectedReport(null);
          setComments("");
        },
      });
    } else if (actionType === 'bid-committee') {
      approveByBidCommittee.mutate({ reportId, data }, {
        onSuccess: () => {
          setApprovalModal(false);
          setSelectedReport(null);
          setComments("");
        },
      });
    }
  };

  const handleReportReject = () => {
    if (!selectedReport || !comments.trim()) return;
    
    const reportId = selectedReport.id;
    const data = { comments: comments.trim() };
    
    if (actionType === 'check') {
      rejectCheck.mutate({ reportId, data }, {
        onSuccess: () => {
          setRejectionModal(false);
          setSelectedReport(null);
          setComments("");
        },
      });
    } else if (actionType === 'approve') {
      rejectApproval.mutate({ reportId, data }, {
        onSuccess: () => {
          setRejectionModal(false);
          setSelectedReport(null);
          setComments("");
        },
      });
    } else if (actionType === 'bid-committee') {
      rejectByBidCommittee.mutate({ reportId, data }, {
        onSuccess: () => {
          setRejectionModal(false);
          setSelectedReport(null);
          setComments("");
        },
      });
    }
  };

  const isAdmin = user?.role === "ADMIN";
  const isHeadOfProcurement = user?.role === "HEAD_OF_PROCUREMENT";
  const isProcurementOfficer = user?.role === "PROCUREMENT_OFFICER";
  const isBidCommitteeMember = user?.role === "BID_COMMITTEE_MEMBER";
  const isCEO = user?.role === "CEO";
  const hasApprovalAccess = isAdmin || isHeadOfProcurement || isProcurementOfficer || isBidCommitteeMember || isCEO;

  if (!hasApprovalAccess) {
    return (
      <Container fluid>
        <Alert color="red" variant="light">
          <Text>You do not have permission to view pending approvals.</Text>
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid>
      <Title order={2} mb="xl">
        Pending Approvals
      </Title>

        {(isLoading || isLoadingSingleSource || isLoadingAwardSheets || isLoadingAgendaReturn || isLoadingCEOEndorsements || isLoadingPss) && (
          <Alert color="blue" variant="light">
            <Text>Loading pending approvals...</Text>
          </Alert>
        )}

        {error && (
          <Alert color="red" variant="light">
            <Text>Failed to load pending approvals: {error.message}</Text>
            <Text size="sm" mt="xs">
              Please check your connection and try again.
            </Text>
          </Alert>
        )}

        {!isLoading && !isLoadingSingleSource && !isLoadingAwardSheets && !isLoadingAgendaReturn && !isLoadingCEOEndorsements && !isLoadingPss && !error && totalPending === 0 && (
          <Alert color="green" variant="light">
            <Text>No pending approvals found.</Text>
          </Alert>
        )}

        {!isLoading && !isLoadingSingleSource && !isLoadingAwardSheets && !isLoadingAgendaReturn && !isLoadingCEOEndorsements && !isLoadingPss && !error && totalPending > 0 && (
          <>
            {/* Tender Approvals Section */}
            {pendingTenders.length > 0 && (
              <>
                <Title order={3} mb="md" mt="xl">
                  Tender Approvals
                </Title>
                <Grid mb="xl">
                  {pendingTenders.map((tender) => (
                <Grid.Col key={tender.id} span={{ base: 12, md: 6, lg: 4 }}>
                  <Card shadow="sm" padding="lg" radius="md" withBorder>
                    <Stack gap="sm">
                      <Group justify="space-between">
                        <Badge color="yellow" variant="light">
                          Pending Approval
                        </Badge>
                        <Text size="xs" color="dimmed">
                          {new Date(tender.updatedAt).toLocaleDateString()}
                        </Text>
                      </Group>

                      <div>
                        <Text fw={500} size="lg" mb="xs">
                          {tender.description}
                        </Text>
                        <Text size="sm" color="dimmed" lineClamp={2}>
                          {tender.referenceNo}
                        </Text>
                      </div>

                      <Group gap="xs">
                        <Badge variant="light" size="sm">
                          {tender.category}
                        </Badge>
                        {tender.documents?.some(
                          (doc: any) => doc.type === "TOR"
                        ) && (
                          <Badge variant="light" size="sm" color="green">
                            Has TOR Document
                          </Badge>
                        )}
                      </Group>

                      <Group gap="xs" align="center">
                        <IconUser size={14} />
                        <Text size="xs" color="dimmed">
                          Submitted by:{" "}
                          {tender.savedBy?.fullName || "Unknown User"}
                        </Text>
                      </Group>

                      <Group gap="xs" align="center">
                        <IconCalendar size={14} />
                        <Text size="xs" color="dimmed">
                          Deadline:{" "}
                          {dayjs(tender.submissionDeadline).format("MMM DD, YYYY HH:mm")}
                        </Text>
                      </Group>

                      <Group gap="xs" mt="md">
                        <Button
                          size="xs"
                          variant="light"
                          color="blue"
                          leftSection={<IconFileText size={14} />}
                          onClick={() => navigate(`/tenders/${tender.id}`)}
                        >
                          View Details
                        </Button>

                        {tender.documents?.some(
                          (doc: any) => doc.type === "TOR"
                        ) && (
                          <Tooltip label="Download TOR Document">
                            <ActionIcon
                              size="sm"
                              variant="light"
                              color="blue"
                              onClick={() => {
                                setSelectedTender(tender);
                                handleDownload();
                              }}
                            >
                              <IconDownload size={14} />
                            </ActionIcon>
                          </Tooltip>
                        )}
                      </Group>

                      <Group gap="xs" mt="sm">
                        <Button
                          size="xs"
                          color="green"
                          leftSection={<IconCheck size={14} />}
                          onClick={() => {
                            setSelectedTender(tender);
                            setActionType('tender');
                            setApprovalModal(true);
                          }}
                        >
                          Approve
                        </Button>
                        <Button
                          size="xs"
                          color="red"
                          variant="light"
                          leftSection={<IconX size={14} />}
                          onClick={() => {
                            setSelectedTender(tender);
                            setActionType('tender');
                            setRejectionModal(true);
                          }}
                        >
                          Reject
                        </Button>
                      </Group>
                    </Stack>
                  </Card>
                </Grid.Col>
                  ))}
                </Grid>
              </>
            )}

            {/* Evaluation Report Approvals Section */}
            {pendingReports.length > 0 && (
              <>
                <Title order={3} mb="md" mt="xl">
                  Evaluation Report Approvals
                </Title>
                <Grid mb="xl">
                  {pendingReports.map((report: any) => {
                    const getApprovalTypeLabel = () => {
                      if (report.approvalType === 'CHECK') return 'Check Required';
                      if (report.approvalType === 'APPROVE') return 'Approval Required';
                      if (report.approvalType === 'BID_COMMITTEE') return 'Bid Committee Approval';
                      return 'Action Required';
                    };

                    const getStatusColor = () => {
                      if (report.status === 'PENDING_CHECK') return 'yellow';
                      if (report.status === 'PENDING_APPROVAL') return 'orange';
                      if (report.status === 'PENDING_BID_COMMITTEE') return 'blue';
                      return 'gray';
                    };

                    return (
                      <Grid.Col key={report.id} span={{ base: 12, md: 6, lg: 4 }}>
                        <Card shadow="sm" padding="lg" radius="md" withBorder>
                          <Stack gap="sm">
                            <Group justify="space-between">
                              <Badge color={getStatusColor()} variant="light">
                                {getApprovalTypeLabel()}
                              </Badge>
                              <Text size="xs" color="dimmed">
                                {new Date(report.updatedAt).toLocaleDateString()}
                              </Text>
                            </Group>

                            <div>
                              <Text fw={500} size="lg" mb="xs">
                                {report.tender?.description || 'Evaluation Report'}
                              </Text>
                              <Text size="sm" color="dimmed" lineClamp={2}>
                                {report.tender?.referenceNo || 'N/A'}
                              </Text>
                            </div>

                            <Group gap="xs" align="center">
                              <IconUser size={14} />
                              <Text size="xs" color="dimmed">
                                Generated by: {report.generatedBy?.fullName || 'Unknown User'}
                              </Text>
                            </Group>

                            {report.checkedBy && (
                              <Group gap="xs" align="center">
                                <IconUser size={14} />
                                <Text size="xs" color="dimmed">
                                  Checked by: {report.checkedBy?.fullName || 'Pending'}
                                </Text>
                              </Group>
                            )}

                            <Group gap="xs" mt="md">
                              <Button
                                size="xs"
                                variant="light"
                                color="blue"
                                leftSection={<IconFileText size={14} />}
                                onClick={() => navigate(`/tenders/${report.tenderId}/evaluation-report`)}
                              >
                                View Report
                              </Button>
                            </Group>

                            <Group gap="xs" mt="sm">
                              <Button
                                size="xs"
                                color="green"
                                leftSection={<IconCheck size={14} />}
                                onClick={() => {
                                  setSelectedReport(report);
                                  setActionType(
                                    report.approvalType === 'CHECK' ? 'check' :
                                    report.approvalType === 'APPROVE' ? 'approve' : 'bid-committee'
                                  );
                                  setApprovalModal(true);
                                }}
                              >
                                {report.approvalType === 'CHECK' ? 'Check' : 'Approve'}
                              </Button>
                              <Button
                                size="xs"
                                color="red"
                                variant="light"
                                leftSection={<IconX size={14} />}
                                onClick={() => {
                                  setSelectedReport(report);
                                  setActionType(
                                    report.approvalType === 'CHECK' ? 'check' :
                                    report.approvalType === 'APPROVE' ? 'approve' : 'bid-committee'
                                  );
                                  setRejectionModal(true);
                                }}
                              >
                                Reject
                              </Button>
                            </Group>
                          </Stack>
                        </Card>
                      </Grid.Col>
                    );
                  })}
                </Grid>
              </>
            )}

            {/* Single Source Approvals Section */}
            {pendingSingleSource.length > 0 && (
              <>
                <Title order={3} mb="md" mt="xl">
                  Single Source Approvals
                </Title>
                <Grid mb="xl">
                  {pendingSingleSource.map((form: SingleSourceFormWithPending) => (
                    <Grid.Col key={form.id} span={{ base: 12, md: 6, lg: 4 }}>
                      <Card shadow="sm" padding="lg" radius="md" withBorder>
                        <Stack gap="sm">
                          <Group justify="space-between">
                            <Badge color="yellow" variant="light">
                              Pending Approval
                            </Badge>
                            <Text size="xs" color="dimmed">
                              {new Date(form.createdAt).toLocaleDateString()}
                            </Text>
                          </Group>

                          <div>
                            <Text fw={500} size="lg" mb="xs">
                              {form.projectName}
                            </Text>
                            <Text size="sm" color="dimmed" lineClamp={2}>
                              {form.formNumber}
                            </Text>
                          </div>

                          <Group gap="xs">
                            <Badge variant="light" size="sm">
                              Head of Procurement
                            </Badge>
                            <Badge variant="light" size="sm" color="blue">
                              {form.vendorName}
                            </Badge>
                          </Group>

                          <Group gap="xs" align="center">
                            <IconUser size={14} />
                            <Text size="xs" color="dimmed" style={dhivehiDisplayStyle(approvalDisplayName(form.submitter))}>
                              Submitted by:{" "}
                              {form.submitter ? approvalDisplayName(form.submitter) : "Unknown user"}
                            </Text>
                          </Group>

                          <Group gap="xs" align="center">
                            <IconFileText size={14} />
                            <Text size="xs" color="dimmed">
                              Cost: {new Intl.NumberFormat('en-US', {
                                style: 'currency',
                                currency: 'MVR',
                              }).format(form.proposedCost)}
                            </Text>
                          </Group>

                          <Group gap="xs" mt="md">
                            <Button
                              size="xs"
                              variant="light"
                              color="blue"
                              leftSection={<IconFileText size={14} />}
                              onClick={() => navigate(`/single-source/${form.id}`)}
                            >
                              View Details
                            </Button>
                            <Button
                              size="xs"
                              variant="light"
                              color="green"
                              leftSection={<IconCheck size={14} />}
                              onClick={() => {
                                setSelectedSingleSourceForm(form);
                                setComments("");
                                setSingleSourceApprovalModal(true);
                              }}
                            >
                              Approve
                            </Button>
                            <Button
                              size="xs"
                              variant="light"
                              color="red"
                              leftSection={<IconX size={14} />}
                              onClick={() => {
                                setSelectedSingleSourceForm(form);
                                setComments("");
                                setSingleSourceRejectionModal(true);
                              }}
                            >
                              Reject
                            </Button>
                          </Group>
                        </Stack>
                      </Card>
                    </Grid.Col>
                  ))}
                </Grid>
              </>
            )}

            {pendingPss.length > 0 && (
              <>
                <Title order={3} mb="md" mt="xl">
                  Project Summary Sheet (PR-03)
                </Title>
                <Grid mb="xl">
                  {pendingPss.map((s: ProjectSummarySheetWithPending) => (
                    <Grid.Col key={s.id} span={{ base: 12, md: 6, lg: 4 }}>
                      <Card shadow="sm" padding="lg" radius="md" withBorder>
                        <Stack gap="sm">
                          <Group justify="space-between">
                            <Badge color="yellow" variant="light">
                              Pending
                            </Badge>
                            <Badge variant="outline" size="sm">
                              {s.approval?.stage === "PEER_CHECK" ? "Peer check" : "Head of Procurement"}
                            </Badge>
                          </Group>
                          <div>
                            <Text fw={500} size="lg" mb="xs">
                              {s.summarySheetNumber}
                            </Text>
                            <Text size="sm" c="dimmed" lineClamp={2}>
                              {s.projectName}
                            </Text>
                          </div>
                          <Group gap="xs" mt="md">
                            <Button
                              size="xs"
                              variant="light"
                              color="blue"
                              leftSection={<IconFileText size={14} />}
                              onClick={() => navigate(`/project-summary-sheets/${s.id}`)}
                            >
                              View details
                            </Button>
                            <Button
                              size="xs"
                              variant="light"
                              color="green"
                              leftSection={<IconCheck size={14} />}
                              onClick={() => {
                                setSelectedPss(s);
                                setPssApproveComments("");
                                setPssApprovalModal(true);
                              }}
                            >
                              Approve
                            </Button>
                            <Button
                              size="xs"
                              variant="light"
                              color="red"
                              leftSection={<IconX size={14} />}
                              onClick={() => {
                                setSelectedPss(s);
                                setPssRejectComments("");
                                setPssRejectionModal(true);
                              }}
                            >
                              Reject
                            </Button>
                          </Group>
                        </Stack>
                      </Card>
                    </Grid.Col>
                  ))}
                </Grid>
              </>
            )}

            {/* Award Sheet Approvals Section */}
            {pendingAwardSheets.length > 0 && (
              <>
                <Title order={3} mb="md" mt="xl">
                  Award Sheet Approvals
                </Title>
                <Grid mb="xl">
                  {pendingAwardSheets.map((approval: any) => (
                    <Grid.Col key={approval.id} span={{ base: 12, md: 6, lg: 4 }}>
                      <Card shadow="sm" padding="lg" radius="md" withBorder>
                        <Stack gap="sm">
                          <Group justify="space-between">
                            <Badge color="yellow" variant="light">
                              {signingBadgeLabel(approval.signingAs, "Pending Approval")}
                            </Badge>
                            <Text size="xs" color="dimmed">
                              {new Date(approval.createdAt).toLocaleDateString()}
                            </Text>
                          </Group>

                          <div>
                            <Text fw={500} size="lg" mb="xs">
                              {approval.awardSheet?.tenderName || 'Award Sheet'}
                            </Text>
                            <Text size="sm" color="dimmed" lineClamp={2}>
                              {approval.awardSheet?.awardSheetNumber || 'N/A'}
                            </Text>
                          </div>

                          <Group gap="xs">
                            <Badge variant="light" size="sm">
                              {approval.awardSheet?.meeting?.title || 'Meeting'}
                            </Badge>
                            <Badge variant="light" size="sm" color="blue">
                              {approval.awardSheet?.vendor || 'N/A'}
                            </Badge>
                          </Group>

                          <Group gap="xs" align="center">
                            <IconFileText size={14} />
                            <Text size="xs" color="dimmed">
                              Cost: {new Intl.NumberFormat('en-US', {
                                style: 'currency',
                                currency: 'MVR',
                              }).format(Number(approval.awardSheet?.proposedCost || 0))}
                            </Text>
                          </Group>

                          <Group gap="xs" mt="md">
                            <Button
                              size="xs"
                              variant="light"
                              color="blue"
                              leftSection={<IconFileText size={14} />}
                              onClick={() => navigate(`/bid-committee/award-sheets/${approval.awardSheetId}`)}
                            >
                              View Details
                            </Button>
                            <Button
                              size="xs"
                              variant="light"
                              color="green"
                              leftSection={<IconCheck size={14} />}
                              onClick={() => {
                                setSelectedAwardSheetApproval(approval);
                                setComments("");
                                setAwardSheetApprovalModal(true);
                              }}
                            >
                              Approve
                            </Button>
                            <Button
                              size="xs"
                              variant="light"
                              color="red"
                              leftSection={<IconX size={14} />}
                              onClick={() => {
                                setSelectedAwardSheetApproval(approval);
                                setComments("");
                                setAwardSheetRejectionModal(true);
                              }}
                            >
                              Reject
                            </Button>
                          </Group>
                        </Stack>
                      </Card>
                    </Grid.Col>
                  ))}
                </Grid>
              </>
            )}

            {pendingAgendaReturns.length > 0 && (
              <>
                <Title order={3} mb="md" mt="xl">
                  Agenda return form votes
                </Title>
                <Grid mb="xl">
                  {pendingAgendaReturns.map((approval: any) => (
                    <Grid.Col key={approval.id} span={{ base: 12, md: 6, lg: 4 }}>
                      <Card shadow="sm" padding="lg" radius="md" withBorder>
                        <Stack gap="sm">
                          <Group justify="space-between">
                            <Badge color="yellow" variant="light">
                              {signingBadgeLabel(approval.signingAs, "Pending vote")}
                            </Badge>
                          </Group>
                          <Text fw={500} size="lg">
                            {approval.agendaReturnForm?.formNumber ?? "Return form"}
                          </Text>
                          <Text size="sm" c="dimmed">
                            {approval.agendaReturnForm?.agendaItem?.subject ?? "Agenda item"}
                          </Text>
                          <Text size="sm" c="dimmed">
                            {approval.agendaReturnForm?.meeting?.title ?? "Meeting"}
                          </Text>
                          <Group gap="xs" mt="md">
                            <Button
                              size="xs"
                              variant="light"
                              color="blue"
                              leftSection={<IconFileText size={14} />}
                              onClick={() =>
                                navigate(
                                  `/bid-committee/agenda-return-forms/${approval.agendaReturnFormId}`
                                )
                              }
                            >
                              View
                            </Button>
                            <Button
                              size="xs"
                              variant="light"
                              color="green"
                              leftSection={<IconCheck size={14} />}
                              onClick={() => {
                                setSelectedAgendaReturnApproval(approval);
                                setComments("");
                                setAgendaReturnApprovalModal(true);
                              }}
                            >
                              Approve
                            </Button>
                            <Button
                              size="xs"
                              variant="light"
                              color="red"
                              leftSection={<IconX size={14} />}
                              onClick={() => {
                                setSelectedAgendaReturnApproval(approval);
                                setComments("");
                                setAgendaReturnRejectionModal(true);
                              }}
                            >
                              Reject
                            </Button>
                          </Group>
                        </Stack>
                      </Card>
                    </Grid.Col>
                  ))}
                </Grid>
              </>
            )}

            {/* CEO Endorsement Approvals Section */}
            {pendingCEOEndorsements.length > 0 && (
              <>
                <Title order={3} mb="md" mt="xl">
                  CEO Endorsement Approvals
                </Title>
                <Grid mb="xl">
                  {pendingCEOEndorsements.map((approval: any) => (
                    <Grid.Col key={approval.id} span={{ base: 12, md: 6, lg: 4 }}>
                      <Card shadow="sm" padding="lg" radius="md" withBorder>
                        <Stack gap="sm">
                          <Group justify="space-between">
                            <Badge color="yellow" variant="light">
                              Pending Approval
                            </Badge>
                            <Text size="xs" color="dimmed">
                              {new Date(approval.createdAt).toLocaleDateString()}
                            </Text>
                          </Group>

                          <div>
                            <Text fw={500} size="lg" mb="xs">
                              CEO Endorsement
                            </Text>
                            <Text size="sm" color="dimmed" lineClamp={2}>
                              {approval.endorsement?.meeting?.title || 'Meeting'}
                            </Text>
                          </div>

                          <Group gap="xs">
                            <Badge variant="light" size="sm">
                              {approval.stage === 'CEO' ? 'CEO Approval' : 'Head of Procurement'}
                            </Badge>
                            <Badge variant="light" size="sm" color="blue">
                              {approval.endorsement?.items?.length || 0} item{approval.endorsement?.items?.length !== 1 ? 's' : ''}
                            </Badge>
                          </Group>

                          <Group gap="xs" align="center">
                            <IconFileText size={14} />
                            <Text size="xs" color="dimmed">
                              Meeting: {approval.endorsement?.meeting?.meetingNumber || 'N/A'}
                            </Text>
                          </Group>

                          <Group gap="xs" mt="md">
                            <Button
                              size="xs"
                              variant="light"
                              color="blue"
                              leftSection={<IconFileText size={14} />}
                              onClick={() => navigate(`/bid-committee/endorsements/${approval.endorsementId}`)}
                            >
                              View Details
                            </Button>
                            <Button
                              size="xs"
                              variant="light"
                              color="green"
                              leftSection={<IconCheck size={14} />}
                              onClick={() => {
                                setSelectedCEOEndorsementApproval(approval);
                                setComments("");
                                setCeoEndorsementApprovalModal(true);
                              }}
                            >
                              Approve
                            </Button>
                            <Button
                              size="xs"
                              variant="light"
                              color="red"
                              leftSection={<IconX size={14} />}
                              onClick={() => {
                                setSelectedCEOEndorsementApproval(approval);
                                setComments("");
                                setCeoEndorsementRejectionModal(true);
                              }}
                            >
                              Reject
                            </Button>
                          </Group>
                        </Stack>
                      </Card>
                    </Grid.Col>
                  ))}
                </Grid>
              </>
            )}
          </>
        )}

      {/* Approval Modal */}
      <Modal
        opened={approvalModal}
        onClose={() => {
          setApprovalModal(false);
          setSelectedTender(null);
          setSelectedReport(null);
          setComments("");
        }}
        title={
          actionType === 'tender' ? 'Approve Tender' :
          actionType === 'check' ? 'Check Evaluation Report' :
          actionType === 'approve' ? 'Approve Evaluation Report' :
          'Approve Evaluation Report (Bid Committee)'
        }
        size="md"
      >
        <Stack gap="md">
          <Text size="sm">
            {actionType === 'tender' && 'Are you sure you want to approve this tender? This will publish it and make it available to bidders.'}
            {actionType === 'check' && 'Are you sure you want to check and approve this evaluation report? This will send it to the Head of Procurement for final approval.'}
            {actionType === 'approve' && 'Are you sure you want to approve this evaluation report? This will move it to the bid committee stage.'}
            {actionType === 'bid-committee' && 'Are you sure you want to approve this evaluation report as a bid committee member?'}
          </Text>

          <Textarea
            label="Comments (Optional)"
            placeholder="Add any comments about the approval..."
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            rows={3}
          />

          <Group justify="flex-end" mt="md">
            <Button
              variant="light"
              onClick={() => {
                setApprovalModal(false);
                setSelectedTender(null);
                setSelectedReport(null);
                setComments("");
              }}
              disabled={
                actionType === 'tender' ? approveTender.isPending :
                actionType === 'check' ? checkReport.isPending :
                actionType === 'approve' ? approveReport.isPending :
                approveByBidCommittee.isPending
              }
            >
              Cancel
            </Button>
            <Button
              color="green"
              onClick={actionType === 'tender' ? handleApprove : handleReportApprove}
              loading={
                actionType === 'tender' ? approveTender.isPending :
                actionType === 'check' ? checkReport.isPending :
                actionType === 'approve' ? approveReport.isPending :
                approveByBidCommittee.isPending
              }
            >
              {actionType === 'tender' ? 'Approve Tender' :
               actionType === 'check' ? 'Check Report' :
               actionType === 'approve' ? 'Approve Report' :
               'Approve Report'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Rejection Modal */}
      <Modal
        opened={rejectionModal}
        onClose={() => {
          setRejectionModal(false);
          setSelectedTender(null);
          setSelectedReport(null);
          setComments("");
        }}
        title={
          actionType === 'tender' ? 'Reject Tender' :
          actionType === 'check' ? 'Reject Evaluation Report Check' :
          actionType === 'approve' ? 'Reject Evaluation Report Approval' :
          'Reject Evaluation Report (Bid Committee)'
        }
        size="md"
      >
        <Stack gap="md">
          <Text size="sm">
            {actionType === 'tender' && 'Are you sure you want to reject this tender? Please provide a reason for the rejection.'}
            {actionType === 'check' && 'Are you sure you want to reject this evaluation report? It will be sent back to the generator. Please provide a reason.'}
            {actionType === 'approve' && 'Are you sure you want to reject this evaluation report? It will be sent back to the generator. Please provide a reason.'}
            {actionType === 'bid-committee' && 'Are you sure you want to reject this evaluation report? It will be sent back to the generator. Please provide a reason.'}
          </Text>

          <Textarea
            label="Rejection Comments"
            placeholder={
              actionType === 'tender' ? 'Please provide a reason for rejecting this tender...' :
              'Please provide a reason for rejecting this evaluation report...'
            }
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            rows={3}
            required
            error={!comments.trim() && "Comments are required"}
          />

          <Group justify="flex-end" mt="md">
            <Button
              variant="light"
              onClick={() => {
                setRejectionModal(false);
                setSelectedTender(null);
                setSelectedReport(null);
                setComments("");
              }}
              disabled={
                actionType === 'tender' ? rejectTender.isPending :
                actionType === 'check' ? rejectCheck.isPending :
                actionType === 'approve' ? rejectApproval.isPending :
                rejectByBidCommittee.isPending
              }
            >
              Cancel
            </Button>
            <Button
              color="red"
              onClick={actionType === 'tender' ? handleReject : handleReportReject}
              loading={
                actionType === 'tender' ? rejectTender.isPending :
                actionType === 'check' ? rejectCheck.isPending :
                actionType === 'approve' ? rejectApproval.isPending :
                rejectByBidCommittee.isPending
              }
              disabled={!comments.trim()}
            >
              {actionType === 'tender' ? 'Reject Tender' :
               actionType === 'check' ? 'Reject Check' :
               actionType === 'approve' ? 'Reject Approval' :
               'Reject Report'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Single Source Approval Modal */}
      <Modal
        opened={singleSourceApprovalModal}
        onClose={() => {
          setSingleSourceApprovalModal(false);
          setSelectedSingleSourceForm(null);
          setComments("");
        }}
        title="Approve Single Source Form"
        size="md"
      >
        <Stack gap="md">
          {selectedSingleSourceForm && (
            <>
              <div>
                <Text size="sm" c="dimmed">Form Number</Text>
                <Text fw={500}>{selectedSingleSourceForm.formNumber}</Text>
              </div>
              <div>
                <Text size="sm" c="dimmed">Title</Text>
                <Text>{selectedSingleSourceForm.projectName}</Text>
              </div>
              <div>
                <Text size="sm" c="dimmed">Submitted by</Text>
                <Text style={dhivehiDisplayStyle(approvalDisplayName(selectedSingleSourceForm.submitter))}>
                  {selectedSingleSourceForm.submitter
                    ? approvalDisplayName(selectedSingleSourceForm.submitter)
                    : "Unknown user"}
                </Text>
              </div>
            </>
          )}
          <Text size="sm">
            Are you sure you want to approve this single source form? This will mark it as approved.
          </Text>
          <Textarea
            label="Comments (Optional)"
            placeholder="Add any comments about your approval..."
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            rows={3}
          />
          <Group justify="flex-end" mt="md">
            <Button
              variant="light"
              onClick={() => {
                setSingleSourceApprovalModal(false);
                setSelectedSingleSourceForm(null);
                setComments("");
              }}
              disabled={approveSingleSourceForm.isPending}
            >
              Cancel
            </Button>
            <Button
              color="green"
              onClick={handleSingleSourceApprove}
              loading={approveSingleSourceForm.isPending}
            >
              Approve
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Single Source Rejection Modal */}
      <Modal
        opened={singleSourceRejectionModal}
        onClose={() => {
          setSingleSourceRejectionModal(false);
          setSelectedSingleSourceForm(null);
          setComments("");
        }}
        title="Reject Single Source Form"
        size="md"
      >
        <Stack gap="md">
          {selectedSingleSourceForm && (
            <>
              <div>
                <Text size="sm" c="dimmed">Form Number</Text>
                <Text fw={500}>{selectedSingleSourceForm.formNumber}</Text>
              </div>
              <div>
                <Text size="sm" c="dimmed">Title</Text>
                <Text>{selectedSingleSourceForm.projectName}</Text>
              </div>
              <div>
                <Text size="sm" c="dimmed">Submitted by</Text>
                <Text style={dhivehiDisplayStyle(approvalDisplayName(selectedSingleSourceForm.submitter))}>
                  {selectedSingleSourceForm.submitter
                    ? approvalDisplayName(selectedSingleSourceForm.submitter)
                    : "Unknown user"}
                </Text>
              </div>
            </>
          )}
          <Text size="sm">
            Are you sure you want to reject this single source form? Please provide a reason for rejection.
          </Text>
          <Textarea
            label="Rejection Comments"
            placeholder="Please provide a reason for rejecting this form..."
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            rows={3}
            required
            error={!comments.trim() && "Comments are required"}
          />
          <Group justify="flex-end" mt="md">
            <Button
              variant="light"
              onClick={() => {
                setSingleSourceRejectionModal(false);
                setSelectedSingleSourceForm(null);
                setComments("");
              }}
              disabled={rejectSingleSourceForm.isPending}
            >
              Cancel
            </Button>
            <Button
              color="red"
              onClick={handleSingleSourceReject}
              loading={rejectSingleSourceForm.isPending}
              disabled={!comments.trim()}
            >
              Reject
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Award Sheet Approval Modal */}
      <Modal
        opened={awardSheetApprovalModal}
        onClose={() => {
          setAwardSheetApprovalModal(false);
          setSelectedAwardSheetApproval(null);
          setComments("");
        }}
        title="Approve Award Sheet"
        size="md"
      >
        <Stack gap="md">
          {selectedAwardSheetApproval && (
            <>
              <div>
                <Text size="sm" c="dimmed">Award Sheet Number</Text>
                <Text fw={500}>{selectedAwardSheetApproval.awardSheet?.awardSheetNumber || 'N/A'}</Text>
              </div>
              <div>
                <Text size="sm" c="dimmed">Tender Name</Text>
                <Text>{selectedAwardSheetApproval.awardSheet?.tenderName || 'N/A'}</Text>
              </div>
            </>
          )}
          <Text size="sm">
            Are you sure you want to approve this award sheet?
          </Text>
          <Textarea
            label="Comments (Optional)"
            placeholder="Add any comments about your approval..."
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            rows={3}
          />
          <Group justify="flex-end" mt="md">
            <Button
              variant="light"
              onClick={() => {
                setAwardSheetApprovalModal(false);
                setSelectedAwardSheetApproval(null);
                setComments("");
              }}
              disabled={approveAwardSheetApproval.isPending}
            >
              Cancel
            </Button>
            <Button
              color="green"
              onClick={handleAwardSheetApprove}
              loading={approveAwardSheetApproval.isPending}
            >
              Approve
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Award Sheet Rejection Modal */}
      <Modal
        opened={awardSheetRejectionModal}
        onClose={() => {
          setAwardSheetRejectionModal(false);
          setSelectedAwardSheetApproval(null);
          setComments("");
        }}
        title="Reject Award Sheet"
        size="md"
      >
        <Stack gap="md">
          {selectedAwardSheetApproval && (
            <>
              <div>
                <Text size="sm" c="dimmed">Award Sheet Number</Text>
                <Text fw={500}>{selectedAwardSheetApproval.awardSheet?.awardSheetNumber || 'N/A'}</Text>
              </div>
              <div>
                <Text size="sm" c="dimmed">Tender Name</Text>
                <Text>{selectedAwardSheetApproval.awardSheet?.tenderName || 'N/A'}</Text>
              </div>
            </>
          )}
          <Text size="sm">
            Are you sure you want to reject this award sheet? Please provide a reason for rejection.
          </Text>
          <Textarea
            label="Rejection Comments"
            placeholder="Please provide a reason for rejecting this award sheet..."
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            rows={3}
            required
            error={!comments.trim() && "Comments are required"}
          />
          <Group justify="flex-end" mt="md">
            <Button
              variant="light"
              onClick={() => {
                setAwardSheetRejectionModal(false);
                setSelectedAwardSheetApproval(null);
                setComments("");
              }}
              disabled={rejectAwardSheetApproval.isPending}
            >
              Cancel
            </Button>
            <Button
              color="red"
              onClick={handleAwardSheetReject}
              loading={rejectAwardSheetApproval.isPending}
              disabled={!comments.trim()}
            >
              Reject
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={agendaReturnApprovalModal}
        onClose={() => {
          setAgendaReturnApprovalModal(false);
          setSelectedAgendaReturnApproval(null);
          setComments("");
        }}
        title="Approve agenda return form"
        size="md"
      >
        <Stack gap="md">
          <Text size="sm">Record your approval as a bid committee member.</Text>
          <Textarea
            label="Comments (optional)"
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            rows={2}
          />
          <Group justify="flex-end">
            <Button variant="light" onClick={() => setAgendaReturnApprovalModal(false)}>
              Cancel
            </Button>
            <Button color="green" onClick={handleAgendaReturnApprove} loading={approveAgendaReturnApproval.isPending}>
              Approve
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={agendaReturnRejectionModal}
        onClose={() => {
          setAgendaReturnRejectionModal(false);
          setSelectedAgendaReturnApproval(null);
          setComments("");
        }}
        title="Reject agenda return form"
        size="md"
      >
        <Stack gap="md">
          <Textarea
            label="Rejection comments"
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            rows={3}
            required
          />
          <Group justify="flex-end">
            <Button variant="light" onClick={() => setAgendaReturnRejectionModal(false)}>
              Cancel
            </Button>
            <Button
              color="red"
              onClick={handleAgendaReturnReject}
              loading={rejectAgendaReturnApproval.isPending}
              disabled={!comments.trim()}
            >
              Reject
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* CEO Endorsement Approval Modal */}
      <Modal
        opened={ceoEndorsementApprovalModal}
        onClose={() => {
          setCeoEndorsementApprovalModal(false);
          setSelectedCEOEndorsementApproval(null);
          setComments("");
        }}
        title="Approve CEO Endorsement"
        size="md"
      >
        <Stack gap="md">
          {selectedCEOEndorsementApproval && (
            <>
              <div>
                <Text size="sm" c="dimmed">Meeting</Text>
                <Text fw={500}>{selectedCEOEndorsementApproval.endorsement?.meeting?.title || 'N/A'}</Text>
              </div>
              <div>
                <Text size="sm" c="dimmed">Stage</Text>
                <Text>{selectedCEOEndorsementApproval.stage === 'CEO' ? 'CEO Approval' : 'Head of Procurement Approval'}</Text>
              </div>
            </>
          )}
          <Text size="sm">
            Are you sure you want to approve this CEO endorsement?
          </Text>
          <Textarea
            label="Comments (Optional)"
            placeholder="Add any comments about your approval..."
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            rows={3}
          />
          <Group justify="flex-end" mt="md">
            <Button
              variant="light"
              onClick={() => {
                setCeoEndorsementApprovalModal(false);
                setSelectedCEOEndorsementApproval(null);
                setComments("");
              }}
              disabled={approveCEOEndorsement.isPending}
            >
              Cancel
            </Button>
            <Button
              color="green"
              onClick={handleCEOEndorsementApprove}
              loading={approveCEOEndorsement.isPending}
            >
              Approve
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={pssApprovalModal}
        onClose={() => {
          setPssApprovalModal(false);
          setSelectedPss(null);
          setPssApproveComments("");
        }}
        title="Approve project summary sheet"
        size="md"
      >
        <Stack gap="md">
          {selectedPss && (
            <Text size="sm" c="dimmed">
              {selectedPss.summarySheetNumber} — {selectedPss.approval?.stage === "PEER_CHECK" ? "Peer check" : "Head of Procurement"}
            </Text>
          )}
          <Textarea
            label="Comments (optional)"
            value={pssApproveComments}
            onChange={(e) => setPssApproveComments(e.currentTarget.value)}
            rows={3}
          />
          <Group justify="flex-end">
            <Button
              variant="light"
              onClick={() => {
                setPssApprovalModal(false);
                setSelectedPss(null);
                setPssApproveComments("");
              }}
            >
              Cancel
            </Button>
            <Button
              color="green"
              onClick={handlePssApprove}
              loading={approvePeerPss.isPending || approveHeadPss.isPending}
            >
              Approve
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={pssRejectionModal}
        onClose={() => {
          setPssRejectionModal(false);
          setSelectedPss(null);
          setPssRejectComments("");
        }}
        title="Reject project summary sheet"
        size="md"
      >
        <Stack gap="md">
          <Textarea
            label="Comments (required)"
            required
            value={pssRejectComments}
            onChange={(e) => setPssRejectComments(e.currentTarget.value)}
            rows={3}
          />
          <Group justify="flex-end">
            <Button variant="light" onClick={() => setPssRejectionModal(false)}>
              Cancel
            </Button>
            <Button
              color="red"
              disabled={!pssRejectComments.trim()}
              loading={rejectPss.isPending}
              onClick={handlePssReject}
            >
              Reject
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* CEO Endorsement Rejection Modal */}
      <Modal
        opened={ceoEndorsementRejectionModal}
        onClose={() => {
          setCeoEndorsementRejectionModal(false);
          setSelectedCEOEndorsementApproval(null);
          setComments("");
        }}
        title="Reject CEO Endorsement"
        size="md"
      >
        <Stack gap="md">
          {selectedCEOEndorsementApproval && (
            <>
              <div>
                <Text size="sm" c="dimmed">Meeting</Text>
                <Text fw={500}>{selectedCEOEndorsementApproval.endorsement?.meeting?.title || 'N/A'}</Text>
              </div>
              <div>
                <Text size="sm" c="dimmed">Stage</Text>
                <Text>{selectedCEOEndorsementApproval.stage === 'CEO' ? 'CEO Approval' : 'Head of Procurement Approval'}</Text>
              </div>
            </>
          )}
          <Text size="sm">
            Are you sure you want to reject this CEO endorsement? Please provide a reason for rejection.
          </Text>
          <Textarea
            label="Rejection Comments"
            placeholder="Please provide a reason for rejecting this endorsement..."
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            rows={3}
            required
            error={!comments.trim() && "Comments are required"}
          />
          <Group justify="flex-end" mt="md">
            <Button
              variant="light"
              onClick={() => {
                setCeoEndorsementRejectionModal(false);
                setSelectedCEOEndorsementApproval(null);
                setComments("");
              }}
              disabled={rejectCEOEndorsement.isPending}
            >
              Cancel
            </Button>
            <Button
              color="red"
              onClick={handleCEOEndorsementReject}
              loading={rejectCEOEndorsement.isPending}
              disabled={!comments.trim()}
            >
              Reject
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}
